import { FC } from 'react';
import { addLocale } from 'primereact/api';

import { Calendar } from 'primereact/calendar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import classNames from 'classnames';
import { Nullable } from 'primereact/ts-helpers';
import { useTranslation } from 'react-i18next';
import styles from './InputDateField.module.scss';

type InputDateFieldProps = {
  id: string;
  label: string;
  name: string;
  value: Nullable<Date>;
  onChange?: (event: Nullable<Date>) => void;
  className?: string;
  readOnlyInput?: boolean;
  dateFormat?: string;
  iconLabel?: IconProp;
  maxDate?: Date;
  inputClassName?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  minDate?: Date;
  showTime?: boolean;
};

addLocale('es', {
  firstDayOfWeek: 1,
  dayNames: [
    'Domingo',
    'Lunes',
    'Martes',
    'Miércoles',
    'Jueves',
    'Viernes',
    'Sábado',
  ],
  dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
  dayNamesMin: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
  monthNames: [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ],
  monthNamesShort: [
    'ene',
    'feb',
    'mar',
    'abr',
    'may',
    'jun',
    'jul',
    'ago',
    'sep',
    'oct',
    'nov',
    'dic',
  ],
  today: 'Hoy',
  clear: 'Limpiar',
});

const InputDateField: FC<InputDateFieldProps> = ({
  id,
  label,
  name,
  value,
  onChange,
  className,
  readOnlyInput,
  dateFormat = 'dd/mm/yy',
  iconLabel,
  maxDate,
  inputClassName,
  placeholder,
  error,
  disabled,
  minDate,
  showTime,
}) => {
  const { i18n } = useTranslation();

  return (
    <div className={classNames(styles.dateField, className)}>
      <label htmlFor={id} style={{ gap: '10px', display: 'flex' }}>
        {iconLabel && <FontAwesomeIcon icon={iconLabel} />}
        <span>{label}</span>
      </label>
      <Calendar
        id={id}
        name={name}
        onChange={(e) => onChange?.(e.value)}
        value={value}
        className={classNames(
          styles.input,
          inputClassName,
          error && styles.invalid
        )}
        showIcon
        readOnlyInput={readOnlyInput}
        dateFormat={dateFormat}
        maxDate={maxDate}
        placeholder={placeholder}
        disabled={disabled}
        minDate={minDate}
        showTime={showTime}
        selectOtherMonths
        locale={i18n.language}
      />
      {error ? <p className={styles.errorMessage}>{error}</p> : null}
    </div>
  );
};

export default InputDateField;
